import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, Ticket, RefreshCw, Trophy, Gift, Wallet, Smartphone } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { BasarCardGrid } from "../components/basar/BasarCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

// Felles «Basar»-oversikt: aktive basarar (opne, innanfor salsperioden) + nyleg trekte. Søk skjer lokalt.
export default function BasarOverviewPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    setError(false);
    api
      .get("/basar", { params: { limit: 200 } })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filter = (list) => {
    const needle = q.trim().toLowerCase();
    return (list || []).filter((b) => !needle || `${b.name} ${b.organizer_name || ""}`.toLowerCase().includes(needle));
  };
  const active = useMemo(() => filter(data?.active), [data, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const drawn = useMemo(() => filter(data?.drawn), [data, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = (data?.active?.length || 0) + (data?.drawn?.length || 0);

  return (
    <PublicLayout>
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><Ticket className="w-5 h-5" /></span>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40" data-testid="basar-overview-kicker">{t("Basar")}</Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" data-testid="basar-overview-title">{t("Basar – kjøp lodd og støtt lokale lag")}</h1>
            <p className="text-base text-muted-foreground leading-relaxed">{t("Velg loddnumrene dine selv, betal med Vipps og få numrene på skjermen med en gang. Trekningen skjer digitalt, og vinnerne publiseres på basarsiden.")}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
              <span className="inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-brand" /> {t("Vipps rett til arrangøren")}</span>
              <span className="inline-flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-brand" /> {t("Premier med verdi")}</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5 text-brand" /> {t("Finn igjen loddene med mobilnummeret")}</span>
            </div>
          </motion.div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-xl">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk etter basar eller arrangør")} className="pl-9 h-11 bg-background/80" aria-label={t("Søk etter basar eller arrangør")} data-testid="basar-overview-search-input" />
              {q && (
                <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150" aria-label={t("Nullstill søk")} data-testid="basar-overview-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            {!loading && !error && <p className="text-sm text-muted-foreground" data-testid="basar-overview-count">{active.length === 1 ? t("{n} aktiv basar", { n: 1 }) : t("{n} aktive basarer", { n: active.length })}</p>}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12" data-testid="basar-overview-page">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="basar-loading-state">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-3" data-testid="basar-error-state">
            <p className="font-heading font-semibold">{t("Kunne ikke laste basarene")}</p>
            <Button onClick={load} variant="outline" data-testid="basar-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button>
          </div>
        ) : active.length === 0 && drawn.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-3" data-testid="basar-empty-state">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><Ticket className="w-7 h-7" /></span>
            <h2 className="font-heading font-semibold text-lg">{total === 0 ? t("Ingen aktive basarer akkurat nå") : t("Ingen basarer passet søket")}</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{total === 0 ? t("Når arrangører åpner loddsalg, dukker basarene opp her.") : t("Prøv et annet søkeord.")}</p>
            {total === 0 ? (
              <Button asChild variant="outline" data-testid="basar-empty-events-link"><Link to="/">{t("Se arrangementer")}</Link></Button>
            ) : (
              <Button variant="outline" onClick={() => setQ("")} data-testid="basar-overview-reset-button">{t("Nullstill søk")}</Button>
            )}
          </div>
        ) : (
          <>
            {active.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-5" data-testid="basar-overview-active-section">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2"><Ticket className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Loddsalg pågår")}</h2>
                <BasarCardGrid basars={active} testId="basar-overview-active-grid" />
              </motion.div>
            )}
            {drawn.length > 0 && (
              <div className="space-y-5" data-testid="basar-overview-drawn-section">
                <div>
                  <h2 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2"><Trophy className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Nylig trukket")}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{t("Vinnerlistene ligger ute i 30 dager etter trekningen.")}</p>
                </div>
                <BasarCardGrid basars={drawn} testId="basar-overview-drawn-grid" />
              </div>
            )}
          </>
        )}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 grid gap-6 sm:grid-cols-3 text-sm" data-testid="basar-overview-howto">
          <div className="space-y-1.5"><span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] font-heading font-semibold">1</span><p className="font-medium">{t("Velg nummer")}</p><p className="text-muted-foreground">{t("Trykk på ledige lodd i rutenettet – eller la oss trekke tilfeldige for deg.")}</p></div>
          <div className="space-y-1.5"><span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] font-heading font-semibold">2</span><p className="font-medium">{t("Betal med Vipps")}</p><p className="text-muted-foreground">{t("Numrene reserveres mens du betaler og blir dine når betalingen er godkjent. Pengene går rett til arrangøren.")}</p></div>
          <div className="space-y-1.5"><span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] font-heading font-semibold">3</span><p className="font-medium">{t("Følg trekningen")}</p><p className="text-muted-foreground">{t("Vinnerne publiseres på basarsiden. Har du oppgitt e-post, får du beskjed hvis du vinner.")}</p></div>
        </div>
      </section>
    </PublicLayout>
  );
}
